import React from 'react';

import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { FormControlComponent } from 'design/formControl';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { PasswordFormValues, PasswordFormViolation } from 'types/state';
import { useServices } from 'hooks/useServices';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextInputControlComponent } from 'design/textInputControl';
import { PressControlComponent } from 'design/pressControl';
import {
  isPasswordFormPasswordRepeatViolation,
  isPasswordFormPasswordViolation,
  validatePasswordFormValues,
} from 'utils/createPasswordFormValidator';
import { ContentLayoutComponent } from 'design/contentLayout';
import { MarginComponent } from 'design/margin';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';

type Props = {
  onSubmitDidSucceed(): void;
};

const initialFormValues: PasswordFormValues = {
  password: '',
  passwordRepeat: '',
};

export const PartnerRegistrationManagerPasswordForm = (props: Props) => {
  const { onSubmitDidSucceed } = props;

  const { submitPartnerPasswordForm } = useServices();

  const intlDictionary = useIntlDictionary();

  const {
    passwordInputPlaceholder,
    repeatPasswordInputPlaceholder,
    partnerRegistrationManager: { passwordForm: ownDictionary },
  } = intlDictionary;

  const [formValues, setFormValues] = React.useState<PasswordFormValues>(
    initialFormValues
  );

  const [formViolations, setFormViolations] = React.useState<
    PasswordFormViolation[]
  >([]);

  const { active: asyncActive, injectPromise } = useAsyncBoundary();

  const submitForm = React.useCallback(() => {
    const violations = validatePasswordFormValues(formValues);

    if (violations.length === 0) {
      const promise = submitPartnerPasswordForm(formValues).then(
        onSubmitDidSucceed
      );

      injectPromise(promise, { shouldShowFallback: false });
    } else {
      setFormViolations(violations);
    }
  }, [
    formValues,
    injectPromise,
    onSubmitDidSucceed,
    submitPartnerPasswordForm,
  ]);

  const setPassword = React.useCallback((nextValue: string) => {
    setFormViolations([]);

    setFormValues((prev) => ({
      ...prev,
      password: nextValue,
    }));
  }, []);

  const setPasswordRepeat = React.useCallback((nextValue: string) => {
    setFormViolations([]);

    setFormValues((prev) => ({
      ...prev,
      passwordRepeat: nextValue,
    }));
  }, []);

  const passwordHint = React.useMemo(() => {
    for (let violation of formViolations) {
      switch (violation.name) {
        case 'PasswordMissed':
          return intlDictionary.inputValidationRequired;
        case 'PasswordTooShort':
          return intlDictionary.inputValidationPassswordLength;
        case 'PasswordMatchedLogin':
          return intlDictionary.inputValidationMatchWithEmail;
      }
    }
  }, [intlDictionary, formViolations]);

  const passwordRepeatHint = React.useMemo(() => {
    for (let violation of formViolations) {
      switch (violation.name) {
        case 'PasswordRepeatMissed':
          return intlDictionary.inputValidationRequired;
        case 'PasswordsDidNotMatch':
          return intlDictionary.inputValidationPasswordsMatch;
      }
    }
  }, [intlDictionary, formViolations]);

  const passwordIsInvalid = React.useMemo(() => {
    return formViolations.some(isPasswordFormPasswordViolation);
  }, [formViolations]);

  const passwordRepeatIsInvalid = React.useMemo(() => {
    return formViolations.some(isPasswordFormPasswordRepeatViolation);
  }, [formViolations]);

  const hasViolatedField = formViolations.length > 0;

  return (
    <FormControlComponent
      onSubmit={submitForm}
      shouldFocusFirstInvalidInput={hasViolatedField}
    >
      <ContentLayoutComponent
        title={
          <TextComponent color="primary" size="l" family="futura">
            {ownDictionary.titleText}
          </TextComponent>
        }
        body={
          <>
            <TextComponent color="primary">
              {ownDictionary.descriptionText}
            </TextComponent>
            <MarginComponent top={28} />
            <FieldsetLayoutComponent>
              <FloatingLabelComponent
                lifted={!!formValues.password}
                invalid={passwordIsInvalid}
                titleText={
                  <TextComponent color="secondary" size="m">
                    {passwordInputPlaceholder}
                  </TextComponent>
                }
                hintText={
                  <TextComponent color="critical" size="s">
                    {passwordHint}
                  </TextComponent>
                }
              >
                <TextInputControlComponent
                  name="password"
                  type="password"
                  autoFocus
                  value={formValues.password}
                  disabled={asyncActive}
                  invalid={passwordIsInvalid}
                  onValueChange={setPassword}
                />
              </FloatingLabelComponent>
              <FloatingLabelComponent
                lifted={!!formValues.passwordRepeat}
                invalid={passwordRepeatIsInvalid}
                titleText={
                  <TextComponent color="secondary" size="m">
                    {repeatPasswordInputPlaceholder}
                  </TextComponent>
                }
                hintText={
                  <TextComponent color="critical" size="s">
                    {passwordRepeatHint}
                  </TextComponent>
                }
              >
                <TextInputControlComponent
                  name="passwordRepeat"
                  type="password"
                  value={formValues.passwordRepeat}
                  disabled={asyncActive}
                  invalid={passwordRepeatIsInvalid}
                  onValueChange={setPasswordRepeat}
                />
              </FloatingLabelComponent>
            </FieldsetLayoutComponent>
            <MarginComponent top={48} />
            <span>
              <PressControlComponent
                disabled={asyncActive}
                hasSpinner={asyncActive}
                type="submit"
                theme="primary"
              >
                {ownDictionary.submitActionText}
              </PressControlComponent>
            </span>
          </>
        }
        backAction={null}
      />
    </FormControlComponent>
  );
};
